import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, X, SlidersHorizontal, RotateCcw, ChevronDown } from "lucide-react";

import { Navbar } from "./Navbar";
import { Footer } from "./Footer";
import { CartDrawer } from "./CartDrawer";
import { CardGateMount } from "./CardGateMount";
import { ChatWidget } from "./ChatWidget";
import { CartProvider } from "../context/CartContext";
import { ProductCard } from "./ProductCard";
import { userApi } from "../lib/api";
import { useUserAuth } from "../context/UserAuthContext";
import { CATEGORIES, expandCategory } from "../lib/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SORT_OPTIONS = [
  { key: "newest", labelKey: "shop_page.sort_newest" },
  { key: "price_asc", labelKey: "shop_page.sort_price_asc" },
  { key: "price_desc", labelKey: "shop_page.sort_price_desc" },
  { key: "name", labelKey: "shop_page.sort_name" },
];

const uniqueSorted = (arr) => Array.from(new Set(arr.filter(Boolean))).sort((a, b) => String(a).localeCompare(String(b)));

const priceBounds = (products) => {
  if (!products.length) return [0, 0];
  let min = Infinity, max = 0;
  for (const p of products) {
    if (typeof p.price === "number") {
      if (p.price < min) min = p.price;
      if (p.price > max) max = p.price;
    }
  }
  if (!isFinite(min)) min = 0;
  return [Math.floor(min), Math.ceil(max)];
};

// A single reusable filter panel (used both in the desktop sidebar and the mobile drawer)
const FilterPanel = ({
  t,
  categoriesUI,
  topLevel,
  subChips,
  activeTop,
  category,
  setCategory,
  minP,
  maxP,
  boundsMin,
  boundsMax,
  setMinP,
  setMaxP,
  inStock,
  setInStock,
  bestseller,
  setBestseller,
  limited,
  setLimited,
  brands,
  selectedBrand,
  setSelectedBrand,
  licenses,
  selectedLicense,
  setSelectedLicense,
  lines,
  selectedLine,
  setSelectedLine,
  onReset,
}) => {
  return (
    <div className="space-y-8">
      {/* Categories */}
      <div>
        <div className="font-heading uppercase text-yellow-400 text-xs tracking-widest mb-3">
          {t("shop_page.filter_categories")}
        </div>
        <div className="flex flex-wrap gap-2">
          {topLevel.map((c) => (
            <button
              key={c.key}
              data-testid={`shop-filter-cat-${c.key.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => setCategory(c.key)}
              className={`px-3 py-1.5 font-heading uppercase tracking-widest text-[11px] border-2 transition ${
                category === c.key
                  ? "bg-yellow-400 text-black border-yellow-400"
                  : "bg-transparent text-zinc-300 border-zinc-700 hover:border-yellow-400 hover:text-yellow-400"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
        {subChips.length > 0 && (
          <div className="mt-3 pl-2 border-l-2 border-yellow-400/30 flex flex-wrap gap-2" data-testid="shop-subfilters">
            {subChips.map((c) => (
              <button
                key={c.key}
                data-testid={`shop-subfilter-${c.key.replace(/\s+/g, "-").toLowerCase()}`}
                onClick={() => setCategory(category === c.key ? activeTop : c.key)}
                className={`px-2.5 py-1 font-heading uppercase tracking-widest text-[10px] border transition ${
                  category === c.key
                    ? "bg-yellow-400 text-black border-yellow-400"
                    : "bg-transparent text-zinc-400 border-zinc-700 hover:border-yellow-400 hover:text-yellow-400"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Price */}
      <div>
        <div className="font-heading uppercase text-yellow-400 text-xs tracking-widest mb-3">
          {t("shop_page.filter_price")}
        </div>
        <div className="flex items-center gap-2">
          <input
            data-testid="shop-filter-price-min"
            type="number"
            inputMode="numeric"
            min={0}
            value={minP}
            onChange={(e) => setMinP(e.target.value)}
            placeholder={`${boundsMin}`}
            className="w-full bg-zinc-900 border-2 border-zinc-800 focus:border-yellow-400 text-white font-mono text-sm px-3 py-2 outline-none"
            aria-label={t("shop_page.filter_price_min")}
          />
          <span className="text-zinc-500 font-mono">—</span>
          <input
            data-testid="shop-filter-price-max"
            type="number"
            inputMode="numeric"
            min={0}
            value={maxP}
            onChange={(e) => setMaxP(e.target.value)}
            placeholder={`${boundsMax}`}
            className="w-full bg-zinc-900 border-2 border-zinc-800 focus:border-yellow-400 text-white font-mono text-sm px-3 py-2 outline-none"
            aria-label={t("shop_page.filter_price_max")}
          />
        </div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-2 font-mono">
          € {boundsMin} — € {boundsMax}
        </div>
      </div>

      {/* Flags */}
      <div>
        <div className="font-heading uppercase text-yellow-400 text-xs tracking-widest mb-3">
          {t("shop_page.filter_flags")}
        </div>
        <div className="space-y-2">
          <label className="flex items-center gap-2 cursor-pointer text-zinc-300 hover:text-yellow-400 transition">
            <input
              type="checkbox"
              data-testid="shop-filter-instock"
              checked={inStock}
              onChange={(e) => setInStock(e.target.checked)}
              className="w-4 h-4 accent-yellow-400"
            />
            <span className="font-heading uppercase tracking-widest text-xs">{t("shop_page.filter_in_stock")}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer text-zinc-300 hover:text-yellow-400 transition">
            <input
              type="checkbox"
              data-testid="shop-filter-bestseller"
              checked={bestseller}
              onChange={(e) => setBestseller(e.target.checked)}
              className="w-4 h-4 accent-yellow-400"
            />
            <span className="font-heading uppercase tracking-widest text-xs">{t("shop_page.filter_bestseller")}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer text-zinc-300 hover:text-yellow-400 transition">
            <input
              type="checkbox"
              data-testid="shop-filter-limited"
              checked={limited}
              onChange={(e) => setLimited(e.target.checked)}
              className="w-4 h-4 accent-yellow-400"
            />
            <span className="font-heading uppercase tracking-widest text-xs">{t("shop_page.filter_limited")}</span>
          </label>
        </div>
      </div>

      {/* Brand */}
      {brands.length > 1 && (
        <div>
          <div className="font-heading uppercase text-yellow-400 text-xs tracking-widest mb-3">
            {t("shop_page.filter_brand")}
          </div>
          <select
            data-testid="shop-filter-brand"
            value={selectedBrand}
            onChange={(e) => setSelectedBrand(e.target.value)}
            className="w-full bg-zinc-900 border-2 border-zinc-800 focus:border-yellow-400 text-white font-heading uppercase tracking-widest text-xs px-3 py-2 outline-none"
          >
            <option value="">{t("shop_page.filter_any")}</option>
            {brands.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
        </div>
      )}

      {/* License */}
      {licenses.length > 1 && (
        <div>
          <div className="font-heading uppercase text-yellow-400 text-xs tracking-widest mb-3">
            {t("shop_page.filter_license")}
          </div>
          <select
            data-testid="shop-filter-license"
            value={selectedLicense}
            onChange={(e) => setSelectedLicense(e.target.value)}
            className="w-full bg-zinc-900 border-2 border-zinc-800 focus:border-yellow-400 text-white font-heading uppercase tracking-widest text-xs px-3 py-2 outline-none"
          >
            <option value="">{t("shop_page.filter_any")}</option>
            {licenses.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </div>
      )}

      {/* Product Line */}
      {lines.length > 1 && (
        <div>
          <div className="font-heading uppercase text-yellow-400 text-xs tracking-widest mb-3">
            {t("shop_page.filter_line")}
          </div>
          <select
            data-testid="shop-filter-line"
            value={selectedLine}
            onChange={(e) => setSelectedLine(e.target.value)}
            className="w-full bg-zinc-900 border-2 border-zinc-800 focus:border-yellow-400 text-white font-heading uppercase tracking-widest text-xs px-3 py-2 outline-none"
          >
            <option value="">{t("shop_page.filter_any")}</option>
            {lines.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </div>
      )}

      <button
        data-testid="shop-filter-reset"
        onClick={onReset}
        className="w-full flex items-center justify-center gap-2 bg-transparent border-2 border-zinc-700 hover:border-yellow-400 text-zinc-300 hover:text-yellow-400 font-heading uppercase tracking-widest text-xs py-2.5 transition"
      >
        <RotateCcw className="w-4 h-4" strokeWidth={3} />
        {t("shop_page.reset")}
      </button>
    </div>
  );
};

const ShopPageInner = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useUserAuth();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wishlistIds, setWishlistIds] = useState([]);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Filters (initialized from URL)
  const [category, setCategory] = useState(searchParams.get("category") || "All");
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [sort, setSort] = useState(searchParams.get("sort") || "newest");
  const [minP, setMinP] = useState("");
  const [maxP, setMaxP] = useState("");
  const [inStock, setInStock] = useState(searchParams.get("stock") === "1");
  const [bestseller, setBestseller] = useState(searchParams.get("best") === "1");
  const [limited, setLimited] = useState(searchParams.get("limited") === "1");
  const [selectedBrand, setSelectedBrand] = useState(searchParams.get("brand") || "");
  const [selectedLicense, setSelectedLicense] = useState(searchParams.get("license") || "");
  const [selectedLine, setSelectedLine] = useState(searchParams.get("line") || "");

  useEffect(() => {
    axios.get(`${API}/products`)
      .then((r) => setProducts(r.data || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!user) { setWishlistIds([]); return; }
    userApi.get("/users/me/wishlist")
      .then(({ data }) => setWishlistIds(data.map((p) => p.id)))
      .catch(() => setWishlistIds([]));
  }, [user]);

  // Keep essential params synced to URL for shareable links
  useEffect(() => {
    const next = new URLSearchParams();
    if (category && category !== "All") next.set("category", category);
    if (query.trim()) next.set("q", query.trim());
    if (sort && sort !== "newest") next.set("sort", sort);
    if (inStock) next.set("stock", "1");
    if (bestseller) next.set("best", "1");
    if (limited) next.set("limited", "1");
    if (selectedBrand) next.set("brand", selectedBrand);
    if (selectedLicense) next.set("license", selectedLicense);
    if (selectedLine) next.set("line", selectedLine);
    setSearchParams(next, { replace: true });
  }, [category, query, sort, inStock, bestseller, limited, selectedBrand, selectedLicense, selectedLine, setSearchParams]);

  const [boundsMin, boundsMax] = useMemo(() => priceBounds(products), [products]);

  // Localized category chips
  const categoriesUI = CATEGORIES.map((c) => {
    const i18nMap = {
      All: t("categories.all"),
      "Action Figure": t("categories.action_figures"),
      Toys: t("categories.toys"),
      "Limited Editions": t("categories.limited"),
      Merchandise: t("categories.merch"),
    };
    return { ...c, label: i18nMap[c.key] || c.label };
  });
  const topLevel = categoriesUI.filter((c) => !c.parent);
  const activeTop = (() => {
    const current = categoriesUI.find((c) => c.key === category);
    if (!current) return null;
    return current.parent || (categoriesUI.some((c) => c.parent === current.key) ? current.key : null);
  })();
  // Only show child chips that actually have matching products in the current catalog
  const categoryCounts = useMemo(() => {
    const map = {};
    for (const p of products) {
      if (p.category) map[p.category] = (map[p.category] || 0) + 1;
    }
    return map;
  }, [products]);
  const subChips = activeTop
    ? categoriesUI.filter((c) => c.parent === activeTop && (categoryCounts[c.key] || 0) > 0)
    : [];

  // Dynamic filter options extracted from data
  const brands = useMemo(() => uniqueSorted(products.map((p) => p.brand)), [products]);
  const licenses = useMemo(() => uniqueSorted(products.map((p) => p.license)), [products]);
  const lines = useMemo(() => uniqueSorted(products.map((p) => p.product_line)), [products]);

  const matchesQuery = (p, tokens) => {
    if (!tokens.length) return true;
    const haystack = [
      p.title, p.category, p.description, p.brand, p.license, p.product_line, p.character_name,
      p.price != null ? `€ ${p.price}` : "",
      p.price != null ? String(p.price) : "",
      p.bestseller ? "bestseller" : "",
      p.limited ? "limited limitata edizione" : "",
    ].filter(Boolean).join(" ").toLowerCase();
    return tokens.every((tok) => haystack.includes(tok));
  };

  const filtered = useMemo(() => {
    let list = products;
    const matchSet = expandCategory(category);
    if (matchSet) list = list.filter((p) => matchSet.has(p.category));
    const q = query.trim().toLowerCase();
    if (q) {
      const tokens = q.split(/\s+/).filter(Boolean);
      list = list.filter((p) => matchesQuery(p, tokens));
    }
    const min = minP === "" ? null : Number(minP);
    const max = maxP === "" ? null : Number(maxP);
    if (min != null && !Number.isNaN(min)) list = list.filter((p) => typeof p.price === "number" && p.price >= min);
    if (max != null && !Number.isNaN(max)) list = list.filter((p) => typeof p.price === "number" && p.price <= max);
    if (inStock) list = list.filter((p) => (p.stock ?? 0) > 0);
    if (bestseller) list = list.filter((p) => !!p.bestseller);
    if (limited) list = list.filter((p) => !!p.limited);
    if (selectedBrand) list = list.filter((p) => p.brand === selectedBrand);
    if (selectedLicense) list = list.filter((p) => p.license === selectedLicense);
    if (selectedLine) list = list.filter((p) => p.product_line === selectedLine);

    const sorted = [...list];
    if (sort === "price_asc") sorted.sort((a, b) => (a.price || 0) - (b.price || 0));
    else if (sort === "price_desc") sorted.sort((a, b) => (b.price || 0) - (a.price || 0));
    else if (sort === "name") sorted.sort((a, b) => String(a.title || "").localeCompare(String(b.title || "")));
    else {
      // newest
      sorted.sort((a, b) => {
        const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
        return tb - ta;
      });
    }
    return sorted;
  }, [products, category, query, minP, maxP, inStock, bestseller, limited, selectedBrand, selectedLicense, selectedLine, sort]);

  const loadWishlist = async () => {
    if (!user) return;
    try {
      const { data } = await userApi.get("/users/me/wishlist");
      setWishlistIds(data.map((p) => p.id));
    } catch { /* ignore wishlist refresh errors */ }
  };

  const onReset = () => {
    setCategory("All");
    setQuery("");
    setSort("newest");
    setMinP("");
    setMaxP("");
    setInStock(false);
    setBestseller(false);
    setLimited(false);
    setSelectedBrand("");
    setSelectedLicense("");
    setSelectedLine("");
  };

  const activeFilterCount = [
    category !== "All",
    !!query.trim(),
    !!minP,
    !!maxP,
    inStock,
    bestseller,
    limited,
    !!selectedBrand,
    !!selectedLicense,
    !!selectedLine,
  ].filter(Boolean).length;

  const filterPanelProps = {
    t, categoriesUI, topLevel, subChips, activeTop,
    category, setCategory, minP, maxP, boundsMin, boundsMax, setMinP, setMaxP,
    inStock, setInStock, bestseller, setBestseller, limited, setLimited,
    brands, selectedBrand, setSelectedBrand,
    licenses, selectedLicense, setSelectedLicense,
    lines, selectedLine, setSelectedLine,
    onReset,
  };

  return (
    <div className="App bg-zinc-950 min-h-screen text-white">
      <Navbar />

      <main className="pt-24">
        {/* Header */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-b-2 border-zinc-900">
          <div className="font-jp text-yellow-400 text-sm tracking-widest mb-3">{t("shop_page.eyebrow")}</div>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl uppercase text-white leading-none">
            {t("shop_page.title_1")} <span className="text-yellow-400">{t("shop_page.title_accent")}</span>
          </h1>
          <p className="text-zinc-400 mt-4 max-w-2xl text-base sm:text-lg">{t("shop_page.subtitle")}</p>
        </section>

        {/* Toolbar */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative flex items-center w-full sm:max-w-md">
            <Search className="absolute left-4 w-5 h-5 text-yellow-400 pointer-events-none" strokeWidth={3} />
            <input
              data-testid="shop-page-search-input"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("shop.search_placeholder")}
              className="w-full bg-zinc-900 border-2 border-zinc-800 focus:border-yellow-400 text-white font-heading uppercase tracking-widest text-sm pl-12 pr-12 py-3.5 outline-none transition"
              aria-label={t("shop.search_placeholder")}
            />
            {query && (
              <button
                type="button"
                data-testid="shop-page-search-clear"
                onClick={() => setQuery("")}
                aria-label={t("shop.search_clear")}
                className="absolute right-3 p-1.5 text-zinc-400 hover:text-yellow-400 transition"
              >
                <X className="w-4 h-4" strokeWidth={3} />
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            <div className="relative">
              <select
                data-testid="shop-page-sort"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="appearance-none bg-zinc-900 border-2 border-zinc-800 hover:border-yellow-400 focus:border-yellow-400 text-white font-heading uppercase tracking-widest text-xs pl-4 pr-10 py-3 outline-none transition cursor-pointer"
                aria-label={t("shop_page.sort_label")}
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.key} value={opt.key}>{t(opt.labelKey)}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-yellow-400 pointer-events-none" strokeWidth={3} />
            </div>

            <button
              data-testid="shop-page-mobile-filters-toggle"
              onClick={() => setMobileFiltersOpen(true)}
              className="lg:hidden flex items-center gap-2 bg-yellow-400 text-black font-heading uppercase tracking-widest text-xs px-4 py-3 border-2 border-yellow-400 hover:bg-black hover:text-yellow-400 transition"
            >
              <SlidersHorizontal className="w-4 h-4" strokeWidth={3} />
              {t("shop_page.filters")}
              {activeFilterCount > 0 && (
                <span data-testid="shop-page-active-filter-count" className="bg-black text-yellow-400 min-w-[20px] h-5 px-1 flex items-center justify-center text-[11px] font-bold">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
        </section>

        {/* Grid + sidebar */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
            {/* Desktop sidebar */}
            <aside data-testid="shop-page-sidebar" className="hidden lg:block sticky top-24 h-fit border-2 border-zinc-800 p-6 bg-zinc-900/40">
              <FilterPanel {...filterPanelProps} />
            </aside>

            {/* Results */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <div data-testid="shop-page-result-count" className="text-xs font-mono uppercase tracking-widest text-zinc-400">
                  {t("shop_page.results", { count: filtered.length })}
                </div>
                {activeFilterCount > 0 && (
                  <button
                    data-testid="shop-page-reset-inline"
                    onClick={onReset}
                    className="text-xs font-heading uppercase tracking-widest text-yellow-400 hover:text-white transition flex items-center gap-1"
                  >
                    <RotateCcw className="w-3 h-3" strokeWidth={3} />
                    {t("shop_page.reset")}
                  </button>
                )}
              </div>

              {loading ? (
                <div data-testid="shop-page-loading" className="text-center py-32 font-heading text-3xl text-zinc-500 uppercase">
                  ...
                </div>
              ) : filtered.length === 0 ? (
                <div data-testid="shop-page-empty" className="text-center py-24 border-2 border-dashed border-zinc-800">
                  <div className="font-heading uppercase text-2xl text-zinc-400 mb-2">
                    {t("shop_page.empty_title")}
                  </div>
                  <div className="text-sm text-zinc-500 mb-6">{t("shop_page.empty_subtitle")}</div>
                  <button
                    data-testid="shop-page-empty-reset"
                    onClick={onReset}
                    className="skew-btn bg-yellow-400 text-black font-heading uppercase text-sm px-6 py-3 border-2 border-yellow-400 hover:bg-black hover:text-yellow-400 transition"
                  >
                    <span>{t("shop_page.reset")}</span>
                  </button>
                </div>
              ) : (
                <div data-testid="shop-page-grid" className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filtered.map((p) => (
                    <ProductCard key={p.id} product={p} wishlistIds={wishlistIds} onWishlistChange={loadWishlist} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* Mobile filters drawer */}
      {mobileFiltersOpen && (
        <div
          data-testid="shop-page-mobile-filters-drawer"
          className="fixed inset-0 z-[60] lg:hidden"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="absolute inset-0 bg-black/70"
            onClick={() => setMobileFiltersOpen(false)}
          />
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-zinc-950 border-l-2 border-yellow-400 overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="font-heading uppercase text-yellow-400 text-lg tracking-widest">
                {t("shop_page.filters")}
              </div>
              <button
                data-testid="shop-page-mobile-filters-close"
                onClick={() => setMobileFiltersOpen(false)}
                aria-label={t("shop_page.close")}
                className="p-2 text-zinc-300 hover:text-yellow-400 transition"
              >
                <X className="w-5 h-5" strokeWidth={3} />
              </button>
            </div>
            <FilterPanel {...filterPanelProps} />
            <button
              data-testid="shop-page-mobile-filters-apply"
              onClick={() => setMobileFiltersOpen(false)}
              className="mt-8 w-full skew-btn bg-yellow-400 text-black font-heading uppercase text-sm px-6 py-3 border-2 border-yellow-400 hover:bg-black hover:text-yellow-400 transition"
            >
              <span>{t("shop_page.show_results", { count: filtered.length })}</span>
            </button>
          </div>
        </div>
      )}

      <CartDrawer />
      <CardGateMount />
      <ChatWidget />
    </div>
  );
};

export default function ShopPage() {
  return (
    <CartProvider>
      <ShopPageInner />
    </CartProvider>
  );
}
